import { createReducer, on } from '@ngrx/store';
import { integrationActions } from '../actions/integration.actions';
import { ICompanyMappingSuggestion } from '../models/integration/company-mapping-suggestion.model';
import { IAumicoCompanyWithMapping } from '../models/integration/connection-mapping.model';
import { IFiscalYear } from '../models/integration/d365bc-import.model';
import { IJournalImportResult } from '../models/integration/journal-import.model';
import { IIntegrationConnection } from '../models/integration/integration-connection.model';
import { IntegrationVendor } from '../models/integration/integration-vendor.enum';
import { IAccountoImportTarget } from '../models/integration/accounto-import.model';
import { IProffixImportTarget } from '../models/integration/proffix-import.model';
import {
    SelectedClosingFolder,
    SharepointConnection,
} from '../components/sharepoint-folders/sharepoint-folders.types';

/**
 * Setup-wizard step. The BE consent-callback does test+create atomically, so we
 * collapse the old `consent_done / awaiting_setup / testing / creating` states.
 */
export type SetupStep = 'idle' | 'awaiting_consent' | 'created' | 'error';

export interface IPendingSetup {
    vendor: IntegrationVendor | null;
    step: SetupStep;
    /** The two pieces of form input the FE submits with the consent request. */
    environmentName: string | null;
    displayName: string | null;
    /** Filled after `consentCompleted` once the connection is fetched. */
    createdConnectionId: string | null;
    /** Set by the BE (or read off the connection) when consent succeeds. */
    tenantIdentifier: string | null;
    error: string | null;
    /**
     * Snapshot of connection IDs that were ALREADY in CONNECTED state for the
     * pending vendor at the moment `initiateConsent` was dispatched. Used by
     * `loadConnectionsSuccess` to distinguish "the new connection just created
     * by this consent flow" from "a connection that already existed before".
     *
     * Multi tenant case: without this, the first poll would return the already
     * connected tenant and the reducer would falsely declare the new flow
     * complete (closing the popup before consent finishes).
     *
     * Known edge case NOT covered: re consenting against the SAME already
     * CONNECTED tenant. Detection times out (3 min) because the id stays in
     * the snapshot. Acceptable — re consent against an already healthy tenant
     * is rare and updating display name in the manage modal is a better path.
     */
    existingConnectedIds: string[];
}

/**
 * BC company list for the manage modal's detail table. Loaded by
 * `loadExternalCompanies` when the user drills into a tenant; cleared when
 * the user navigates away or disconnects. Disjoint from the single edit
 * session below: opening / closing the single edit modal NEVER mutates this
 * slice. (Earlier versions conflated the two and required a workaround to
 * refetch on every edit; that's gone now.)
 */
export interface IExternalCompaniesState {
    connectionId: string | null;
    items: ICompanyMappingSuggestion[];
    isLoading: boolean;
    error: string | null;
}

/**
 * Active single edit session: ONE BC company being remapped via the single
 * edit modal. Owned by `mappingStartSingleEdit` (sets) and `mappingCancel`
 * (resets). The modal's open/close visibility is tied to `isOpen`.
 */
export interface ISingleEditState {
    connectionId: string | null;
    /** Which vendor this single-edit session writes its mapping for. */
    vendor: IntegrationVendor | null;
    suggestion: ICompanyMappingSuggestion | null;
    isOpen: boolean;
}

/**
 * Tenant detail data (D2 use case): which Aumico Companies are bound to a given
 * BC connection, plus their current mapping payload. Loaded lazily when the
 * manage modal drills into a tenant.
 */
export interface ITenantDetailState {
    connectionId: string | null;
    aumicoCompanies: IAumicoCompanyWithMapping[];
    isLoading: boolean;
    error: string | null;
}

/**
 * D365 BC journal import state. Mirrors the Bexio import slice in
 * `aumico-lib-companies-fe`: fiscal years loaded eagerly when the bilance
 * sheet import page has an active D365 BC mapping, then status + result
 * for the actual import HTTP call. Modals on the page subscribe to
 * `importStatus` ('loading' shows spinner, 'success' shows summary).
 */
export interface ID365BcJournalImportState {
    fiscalYears: IFiscalYear[];
    fiscalYearsLoading: boolean;
    fiscalYearsError: string | null;

    importStatus: 'idle' | 'loading' | 'success' | 'error';
    importResult: IJournalImportResult | null;
    importError: string | null;
}

/**
 * Abacus journal-import slice. Same shape as D365 BC minus `fiscalYears*`
 * because Abacus has no discovery endpoint — the caller derives dates from
 * the active Annual Report's General Info and passes them in the body.
 */
export interface IAbacusJournalImportState {
    importStatus: 'idle' | 'loading' | 'success' | 'error';
    importResult: IJournalImportResult | null;
    importError: string | null;
}

/** Topal journal-import slice. Same shape as Abacus (no fiscal-year discovery). */
export interface ITopalJournalImportState {
    importStatus: 'idle' | 'loading' | 'success' | 'error';
    importResult: IJournalImportResult | null;
    importError: string | null;
}

/** Accounto journal-import slice. Same shape as Topal (no fiscal-year discovery). */
export interface IAccountoJournalImportState {
    importStatus: 'idle' | 'loading' | 'success' | 'error';
    importResult: IJournalImportResult | null;
    importError: string | null;
    /** The report the running/finished import belongs to; `null` after a reset (see `accountoImportResultForReport`). */
    importFor: IAccountoImportTarget | null;
}

/** Proffix journal-import slice. Same shape as Topal (no fiscal-year discovery). */
export interface IProffixJournalImportState {
    importStatus: 'idle' | 'loading' | 'success' | 'error';
    importResult: IJournalImportResult | null;
    importError: string | null;
    /** The report the running/finished import belongs to; `null` after a reset (see `proffixImportResultForReport`). */
    importFor: IProffixImportTarget | null;
}

/**
 * Auto match state: drives the banner in the manage modal detail view.
 * Transient: lost on page reload. The persistent record of what auto match did
 * lives in the mappings themselves (DB), this slice just stores the count for
 * the "X automatisch erkannt, Y nicht verknüpft" banner immediately after a run.
 */
export interface IAutoMatchState {
    /** True while the matcher + bulk upsert are in flight. */
    isRunning: boolean;
    /** Which connection the current/last run was for. Null before any run. */
    connectionId: string | null;
    /** Filled by `autoMatchPersisted`. Null until the first run completes. */
    lastResult: {
        autoMatched: number;
        notMapped: number;
        errors: number;
    } | null;
    /** Filled by `autoMatchFailed`. Per spec Option 1 there is no retry path. */
    error: string | null;
}

/**
 * SharePoint / Microsoft 365 document-source slice (REV-21679). A DEDICATED
 * sub-slice — SharePoint is a document source, not an accounting vendor, so it
 * is intentionally kept out of the `connections` map and the IntegrationVendor
 * enum. `connection` carries the pure-view `SharepointConnection` shape plus the
 * BE `connectionId` the picker-token + folder writes need.
 */
export interface ISharepointState {
    connection: SharepointConnection & { connectionId?: string };
    folders: SelectedClosingFolder[];
    /** BE-minted Microsoft File Picker access token (transient; fetched on demand). */
    pickerToken?: string;
    /**
     * Tenant SharePoint Online origin the File Picker must be opened against, delivered
     * alongside the picker token. `undefined` = not fetched yet, `null` = the BE could not
     * discover it (the container then declines to open the picker).
     */
    pickerBaseUrl?: string | null;
    /**
     * TERMINAL failure marker for the picker-token fetch (REV-21057 BUG 2).
     *
     * The container opens the File Picker popup synchronously inside the click and keeps it
     * open while the token/baseUrl are still in flight — so it needs to distinguish "not
     * there YET" (keep waiting) from "will never arrive" (close the popup). `undefined` =
     * no failure; a string = the request failed. Reset on every new fetch.
     */
    pickerTokenError?: string;
    isLoading: boolean;
    error?: string;
}

export interface IntegrationState {
    connections: { [connectionId: string]: IIntegrationConnection };
    connectionIds: string[];
    isLoading: boolean;
    loadError: string | null;

    pendingSetup: IPendingSetup;
    externalCompanies: IExternalCompaniesState;
    singleEdit: ISingleEditState;
    tenantDetail: ITenantDetailState;
    autoMatch: IAutoMatchState;
    d365bcJournalImport: ID365BcJournalImportState;
    abacusJournalImport: IAbacusJournalImportState;
    topalJournalImport: ITopalJournalImportState;
    accountoJournalImport: IAccountoJournalImportState;
    proffixJournalImport: IProffixJournalImportState;
    sharepoint: ISharepointState;
}

export const initialIntegrationState: IntegrationState = {
    connections: {},
    connectionIds: [],
    isLoading: false,
    loadError: null,
    pendingSetup: {
        vendor: null,
        step: 'idle',
        environmentName: null,
        displayName: null,
        createdConnectionId: null,
        tenantIdentifier: null,
        error: null,
        existingConnectedIds: [],
    },
    externalCompanies: {
        connectionId: null,
        items: [],
        isLoading: false,
        error: null,
    },
    singleEdit: {
        connectionId: null,
        vendor: null,
        suggestion: null,
        isOpen: false,
    },
    tenantDetail: {
        connectionId: null,
        aumicoCompanies: [],
        isLoading: false,
        error: null,
    },
    autoMatch: {
        isRunning: false,
        connectionId: null,
        lastResult: null,
        error: null,
    },
    d365bcJournalImport: {
        fiscalYears: [],
        fiscalYearsLoading: false,
        fiscalYearsError: null,
        importStatus: 'idle',
        importResult: null,
        importError: null,
    },
    abacusJournalImport: {
        importStatus: 'idle',
        importResult: null,
        importError: null,
    },
    topalJournalImport: {
        importStatus: 'idle',
        importResult: null,
        importError: null,
    },
    accountoJournalImport: {
        importStatus: 'idle',
        importResult: null,
        importError: null,
        importFor: null,
    },
    proffixJournalImport: {
        importStatus: 'idle',
        importResult: null,
        importError: null,
        importFor: null,
    },
    sharepoint: {
        connection: { status: 'disconnected' },
        folders: [],
        pickerToken: undefined,
        pickerBaseUrl: undefined,
        pickerTokenError: undefined,
        isLoading: false,
        error: undefined,
    },
};

/**
 * Locate THE NEW connection produced by the in flight consent flow.
 *
 * "New" here means: a CONNECTED connection for the pending vendor whose ID
 * was NOT already CONNECTED at the time the consent flow started (i.e. not in
 * `existingConnectedIds`). This is what makes multi tenant connect work: with
 * Tenant A already CONNECTED, the first poll returns A in the list, but A's
 * ID is in the snapshot, so the matcher skips it and waits for B to appear.
 *
 * Re run case (existing connection was in ERROR/EXPIRED/DISCONNECTED and the
 * user re consents): that connection's ID is NOT in the "already CONNECTED"
 * snapshot, so once it flips to CONNECTED on the next poll, it qualifies.
 */
const pickNewConnectionForPendingSetup = (
    connections: IIntegrationConnection[],
    vendor: IntegrationVendor | null,
    existingConnectedIds: ReadonlyArray<string>,
): IIntegrationConnection | null => {
    if (vendor == null) return null;
    const already = new Set(existingConnectedIds);
    const candidates = connections.filter(
        (c) => c.vendor === vendor && c.status === 'CONNECTED' && !already.has(c.id),
    );
    if (candidates.length === 0) return null;
    // Defensive sort: if somehow two candidates qualify, take the newest.
    return candidates.reduce((newest, c) => {
        const newestTs = new Date(newest.createdAt ?? newest.connectedAt).getTime();
        const cTs = new Date(c.createdAt ?? c.connectedAt).getTime();
        return cTs > newestTs ? c : newest;
    });
};

/**
 * The connect POST answered with THE connection this flow produced, so it may complete the setup even
 * if its id was already CONNECTED at flow start: an Accounto key rotation (REV-21119 D7, QRM A3) updates
 * the SAME row and keeps its id. Dropping that one id from the snapshot lets the next
 * `loadConnectionsSuccess` pick it; every other already CONNECTED tenant stays excluded. A success that
 * arrives after Cancel (setup idle) changes nothing.
 */
const releaseReturnedConnection = (
    state: IntegrationState,
    { connection }: { connection: IIntegrationConnection },
): IntegrationState =>
    state.pendingSetup.step === 'awaiting_consent' &&
    state.pendingSetup.vendor === connection.vendor &&
    state.pendingSetup.existingConnectedIds.includes(connection.id)
        ? {
              ...state,
              pendingSetup: {
                  ...state.pendingSetup,
                  existingConnectedIds: state.pendingSetup.existingConnectedIds.filter((id) => id !== connection.id),
              },
          }
        : state;

export const integrationReducer = createReducer<IntegrationState>(
    initialIntegrationState,

    // ── Load Connections ──
    on(integrationActions.loadConnections, (state) => ({
        ...state,
        isLoading: true,
        loadError: null,
    })),
    on(integrationActions.loadConnectionsSuccess, (state, { connections }) => {
        const map: { [id: string]: IIntegrationConnection } = {};
        const ids: string[] = [];
        for (const conn of connections) {
            map[conn.id] = conn;
            ids.push(conn.id);
        }

        // Look for a NEW connection that came online during this consent flow.
        // The snapshot `pendingSetup.existingConnectedIds` excludes connections
        // that were already CONNECTED before the user clicked Verbinden, so a
        // multi tenant flow doesn't false-positive on the existing tenant
        // showing up in the first poll response.
        let pendingSetup = state.pendingSetup;
        if (pendingSetup.step === 'awaiting_consent' && pendingSetup.createdConnectionId == null) {
            const candidate = pickNewConnectionForPendingSetup(
                connections,
                pendingSetup.vendor,
                pendingSetup.existingConnectedIds,
            );
            if (candidate != null) {
                pendingSetup = {
                    ...pendingSetup,
                    step: 'created',
                    createdConnectionId: candidate.id,
                    tenantIdentifier:
                        candidate.d365bc?.vendorTenantIdentifier ?? pendingSetup.tenantIdentifier,
                };
            }
        }

        return {
            ...state,
            connections: map,
            connectionIds: ids,
            isLoading: false,
            loadError: null,
            pendingSetup,
        };
    }),
    on(integrationActions.loadConnectionsFailure, (state, { error }) => ({
        ...state,
        isLoading: false,
        loadError: error,
    })),

    // ── Setup wizard ──
    on(integrationActions.initiateConsent, (state, { vendor, environmentName, displayName }) => {
        // Snapshot the IDs of connections that are ALREADY CONNECTED for this
        // vendor at flow start. The detection in `loadConnectionsSuccess` uses
        // this to identify THE NEW connection (id not in this set) vs. an
        // existing one (id already here) — see `pickNewConnectionForPendingSetup`
        // for why this matters in multi tenant.
        const existingConnectedIds = state.connectionIds
            .map((id) => state.connections[id])
            .filter(
                (c): c is IIntegrationConnection =>
                    c != null && c.vendor === vendor && c.status === 'CONNECTED',
            )
            .map((c) => c.id);

        return {
            ...state,
            pendingSetup: {
                vendor,
                step: 'awaiting_consent',
                environmentName,
                displayName: displayName ?? null,
                createdConnectionId: null,
                tenantIdentifier: null,
                error: null,
                existingConnectedIds,
            },
        };
    }),
    on(integrationActions.consentPopupOpened, (state) => ({
        ...state,
        pendingSetup: { ...state.pendingSetup, step: 'awaiting_consent' },
    })),
    on(integrationActions.consentCompleted, (state) => state), // step transition happens on loadConnectionsSuccess
    on(integrationActions.consentCancelled, (state) => ({
        ...state,
        pendingSetup: { ...state.pendingSetup, step: 'idle', error: null },
    })),
    on(integrationActions.consentFailed, (state, { error }) => ({
        ...state,
        pendingSetup: { ...state.pendingSetup, step: 'error', error },
    })),

    // ── SharePoint ORG connect (REV-21860) ────────────────────────────────
    // Same shape as `initiateConsent`, so the vendor-agnostic detection in
    // `loadConnectionsSuccess` picks up the new SharePoint connection and flips
    // the step to `created`. `environmentName` is '' — SharePoint has no
    // environment concept (the tenant comes from the user's Microsoft sign-in).
    on(integrationActions.sharepointOrgConnectInitiate, (state, { displayName }) => {
        const existingConnectedIds = state.connectionIds
            .map((id) => state.connections[id])
            .filter(
                (c): c is IIntegrationConnection =>
                    c != null &&
                    c.vendor === IntegrationVendor.SHAREPOINT &&
                    c.status === 'CONNECTED',
            )
            .map((c) => c.id);

        return {
            ...state,
            pendingSetup: {
                vendor: IntegrationVendor.SHAREPOINT,
                step: 'awaiting_consent' as const,
                environmentName: '',
                displayName: displayName ?? null,
                createdConnectionId: null,
                tenantIdentifier: null,
                error: null,
                existingConnectedIds,
            },
        };
    }),

    // ── Abacus connect path (no popup, mirrors D365 BC setup wizard) ──────
    on(integrationActions.initiateAbacusConnect, (state, { body }) => {
        // Same multi tenant safeguard as D365 BC: snapshot the IDs of already
        // CONNECTED Abacus connections so the post connect detection picks the
        // NEW one (not an existing one re-emitted by loadConnections).
        const existingConnectedIds = state.connectionIds
            .map((id) => state.connections[id])
            .filter(
                (c): c is IIntegrationConnection =>
                    c != null &&
                    c.vendor === IntegrationVendor.ABACUS &&
                    c.status === 'CONNECTED',
            )
            .map((c) => c.id);

        return {
            ...state,
            pendingSetup: {
                vendor: IntegrationVendor.ABACUS,
                step: 'awaiting_consent',
                // D365 BC specific fields — null for Abacus path.
                environmentName: null,
                // Display name is shared (both vendors use it for the same purpose).
                displayName: body.displayName,
                createdConnectionId: null,
                tenantIdentifier: null,
                error: null,
                existingConnectedIds,
            },
        };
    }),
    on(integrationActions.abacusConnectSuccess, releaseReturnedConnection), // detection happens on loadConnectionsSuccess
    on(integrationActions.abacusConnectFailure, (state, { error }) => ({
        ...state,
        pendingSetup: { ...state.pendingSetup, step: 'error', error },
    })),

    // ── Topal connect path (no popup, mirrors Abacus) ─────────────────────
    on(integrationActions.initiateTopalConnect, (state, { body }) => {
        // Same multi-tenant safeguard: snapshot the IDs of already CONNECTED Topal
        // connections so the post-connect detection picks the NEW one.
        const existingConnectedIds = state.connectionIds
            .map((id) => state.connections[id])
            .filter(
                (c): c is IIntegrationConnection =>
                    c != null &&
                    c.vendor === IntegrationVendor.TOPAL &&
                    c.status === 'CONNECTED',
            )
            .map((c) => c.id);

        return {
            ...state,
            pendingSetup: {
                vendor: IntegrationVendor.TOPAL,
                step: 'awaiting_consent',
                // D365 BC specific field — null for Topal path.
                environmentName: null,
                displayName: body.displayName,
                createdConnectionId: null,
                tenantIdentifier: null,
                error: null,
                existingConnectedIds,
            },
        };
    }),
    on(integrationActions.topalConnectSuccess, releaseReturnedConnection), // detection happens on loadConnectionsSuccess
    on(integrationActions.topalConnectFailure, (state, { error }) => ({
        ...state,
        pendingSetup: { ...state.pendingSetup, step: 'error', error },
    })),
    on(integrationActions.topalConnectAlreadyExists, (state) => ({
        ...state,
        pendingSetup: initialIntegrationState.pendingSetup,
    })),

    // ── Accounto connect path (no popup, mirrors Topal) ───────────────────
    on(integrationActions.initiateAccountoConnect, (state, { body }) => {
        // Same multi-tenant safeguard: snapshot the IDs of already CONNECTED Accounto
        // connections so the post-connect detection picks the NEW one.
        const existingConnectedIds = state.connectionIds
            .map((id) => state.connections[id])
            .filter(
                (c): c is IIntegrationConnection =>
                    c != null &&
                    c.vendor === IntegrationVendor.ACCOUNTO &&
                    c.status === 'CONNECTED',
            )
            .map((c) => c.id);

        return {
            ...state,
            pendingSetup: {
                vendor: IntegrationVendor.ACCOUNTO,
                step: 'awaiting_consent',
                // D365 BC specific field — null for Accounto path.
                environmentName: null,
                displayName: body.displayName,
                createdConnectionId: null,
                tenantIdentifier: null,
                error: null,
                existingConnectedIds,
            },
        };
    }),
    on(integrationActions.accountoConnectSuccess, releaseReturnedConnection), // detection happens on loadConnectionsSuccess
    on(integrationActions.accountoConnectFailure, (state, { error }) => ({
        ...state,
        pendingSetup: { ...state.pendingSetup, step: 'error', error },
    })),
    on(integrationActions.accountoConnectAlreadyExists, (state) => ({
        ...state,
        pendingSetup: initialIntegrationState.pendingSetup,
    })),

    // ── Proffix connect path (no popup, mirrors Topal) ────────────────────
    on(integrationActions.initiateProffixConnect, (state, { body }) => {
        // Same multi-tenant safeguard: snapshot the IDs of already CONNECTED Proffix
        // connections so the post-connect detection picks the NEW one.
        const existingConnectedIds = state.connectionIds
            .map((id) => state.connections[id])
            .filter(
                (c): c is IIntegrationConnection =>
                    c != null &&
                    c.vendor === IntegrationVendor.PROFFIX &&
                    c.status === 'CONNECTED',
            )
            .map((c) => c.id);

        return {
            ...state,
            pendingSetup: {
                vendor: IntegrationVendor.PROFFIX,
                step: 'awaiting_consent',
                // D365 BC specific field — null for Proffix path.
                environmentName: null,
                displayName: body.displayName,
                createdConnectionId: null,
                tenantIdentifier: null,
                error: null,
                existingConnectedIds,
            },
        };
    }),
    on(integrationActions.proffixConnectSuccess, releaseReturnedConnection), // detection happens on loadConnectionsSuccess
    on(integrationActions.proffixConnectFailure, (state, { error }) => ({
        ...state,
        pendingSetup: { ...state.pendingSetup, step: 'error', error },
    })),
    on(integrationActions.proffixConnectAlreadyExists, (state) => ({
        ...state,
        pendingSetup: initialIntegrationState.pendingSetup,
    })),

    on(integrationActions.cancelSetup, (state) => ({
        ...state,
        pendingSetup: initialIntegrationState.pendingSetup,
    })),

    // Already-exists is terminal + non-retryable: wipe pendingSetup back to idle
    // (same as cancel) so the connect modal leaves its loading/error state. The
    // overview subscriber then closes the connect modal and reopens the manage list.
    on(integrationActions.abacusConnectAlreadyExists, (state) => ({
        ...state,
        pendingSetup: initialIntegrationState.pendingSetup,
    })),

    // ── Existing-connection actions ──
    on(integrationActions.testExistingConnectionSuccess, (state, { connectionId, lastTestAt }) => {
        const existing = state.connections[connectionId];
        if (!existing) return state;
        return {
            ...state,
            connections: {
                ...state.connections,
                [connectionId]: { ...existing, lastTestAt, lastErrorMessage: null },
            },
        };
    }),
    on(integrationActions.testExistingConnectionFailure, (state, { connectionId, error }) => {
        const existing = state.connections[connectionId];
        if (!existing) return state;
        return {
            ...state,
            connections: {
                ...state.connections,
                [connectionId]: { ...existing, lastErrorMessage: error },
            },
        };
    }),

    on(integrationActions.disconnectSuccess, (state, { connectionId }) => {
        const newConnections = { ...state.connections };
        delete newConnections[connectionId];
        return {
            ...state,
            connections: newConnections,
            connectionIds: state.connectionIds.filter((id) => id !== connectionId),
        };
    }),

    // ── External companies (manage modal's BC list) ──────────────────────
    //
    // Owned by loadExternalCompanies. Disjoint from the single edit slice
    // below: starting / closing a single edit session never touches this.
    on(integrationActions.loadExternalCompanies, (state, { connectionId }) => ({
        ...state,
        externalCompanies: {
            connectionId,
            // Preserve the previous list while a refresh is in flight so the
            // table doesn't blank out (same pattern as tenantDetail below).
            items:
                state.externalCompanies.connectionId === connectionId
                    ? state.externalCompanies.items
                    : [],
            isLoading: true,
            error: null,
        },
    })),
    on(
        integrationActions.loadExternalCompaniesSuccess,
        (state, { connectionId, suggestions }) => ({
            ...state,
            externalCompanies: {
                connectionId,
                items: suggestions,
                isLoading: false,
                error: null,
            },
        }),
    ),
    on(integrationActions.loadExternalCompaniesFailure, (state, { error }) => ({
        ...state,
        externalCompanies: { ...state.externalCompanies, isLoading: false, error },
    })),

    // ── Single edit session (one BC company in flight) ───────────────────
    //
    // Owned by mappingStartSingleEdit. mappingCancel closes the session.
    on(integrationActions.mappingStartSingleEdit, (state, { connectionId, vendor, suggestion }) => ({
        ...state,
        singleEdit: {
            connectionId,
            vendor,
            suggestion,
            isOpen: true,
        },
    })),
    on(integrationActions.mappingCancel, (state) => ({
        ...state,
        singleEdit: initialIntegrationState.singleEdit,
    })),

    // ── Tenant detail (D2) ──────────────────────────────────────────────────
    on(integrationActions.loadMappedCompanies, (state, { connectionId }) => ({
        ...state,
        tenantDetail: {
            connectionId,
            // Keep the previous list visible while the refresh is in flight so
            // the table doesn't blank out on every Edit/Unlink reload.
            aumicoCompanies:
                state.tenantDetail.connectionId === connectionId
                    ? state.tenantDetail.aumicoCompanies
                    : [],
            isLoading: true,
            error: null,
        },
    })),
    on(integrationActions.loadMappedCompaniesSuccess, (state, { connectionId, companies }) => ({
        ...state,
        tenantDetail: {
            connectionId,
            aumicoCompanies: companies,
            isLoading: false,
            error: null,
        },
    })),
    on(integrationActions.loadMappedCompaniesFailure, (state, { error }) => ({
        ...state,
        tenantDetail: { ...state.tenantDetail, isLoading: false, error },
    })),

    // ── Mapping CRUD success: keep the tenantDetail cache in sync ──────────
    //
    // Effects handle the HTTP call; we only react to *Success here to update
    // the cached list. On upsert we replace/insert the row; on delete we drop
    // it. Effects ALSO dispatch a fresh `loadMappedCompanies` so the server
    // remains authoritative for ordering: these reducer side patches just
    // make the optimistic in between state look right.
    on(integrationActions.upsertMappingSuccess, (state, { companyId, mapping }) => {
        if (state.tenantDetail.connectionId !== mapping.connectionId) return state;
        const existing = state.tenantDetail.aumicoCompanies;
        const existingIdx = existing.findIndex((c) => c.id === companyId);
        let next: IAumicoCompanyWithMapping[];
        if (existingIdx >= 0) {
            next = existing.map((c) =>
                c.id === companyId ? { ...c, mapping } : c,
            );
        } else {
            // We don't have the Aumico Company's name in this action: the
            // effect will dispatch loadMappedCompanies right after, so this
            // gap is < 1 RTT. We add a stub row so the count doesn't briefly
            // drop in the UI.
            next = [...existing, { id: companyId, name: '', mapping }];
        }
        return {
            ...state,
            tenantDetail: { ...state.tenantDetail, aumicoCompanies: next },
        };
    }),
    // ── Auto match ─────────────────────────────────────────────────────────
    on(integrationActions.autoMatchTrigger, (state, { connectionId }) => ({
        ...state,
        autoMatch: {
            isRunning: true,
            connectionId,
            lastResult: null,
            error: null,
        },
    })),
    on(
        integrationActions.autoMatchPersisted,
        (state, { connectionId, autoMatched, notMapped, errors, successfulMappings }) => {
            // Optimistic tenantDetail patch: when the bulk upsert response says
            // a mapping was written we don't need to wait on a follow up read
            // (which races BE eventual consistency). Patch in place so the
            // detail table reflects the new mappings immediately.
            //
            // Only patch when the tenantDetail is for the SAME connection: if
            // the user navigated away between trigger and persisted, leave the
            // detail alone (they'll see the fresh data when they navigate back).
            let nextTenantDetail = state.tenantDetail;
            if (
                state.tenantDetail.connectionId === connectionId &&
                successfulMappings.length > 0
            ) {
                const byId = new Map(
                    state.tenantDetail.aumicoCompanies.map((c) => [c.id, c]),
                );
                for (const sm of successfulMappings) {
                    byId.set(sm.aumicoCompanyId, {
                        id: sm.aumicoCompanyId,
                        name: sm.aumicoCompanyName,
                        mapping: sm.mapping,
                    });
                }
                nextTenantDetail = {
                    ...state.tenantDetail,
                    aumicoCompanies: [...byId.values()],
                };
            }
            return {
                ...state,
                autoMatch: {
                    isRunning: false,
                    connectionId,
                    lastResult: { autoMatched, notMapped, errors },
                    error: null,
                },
                tenantDetail: nextTenantDetail,
            };
        },
    ),
    on(integrationActions.autoMatchFailed, (state, { connectionId, error }) => ({
        ...state,
        autoMatch: {
            isRunning: false,
            connectionId,
            lastResult: null,
            error,
        },
    })),

    on(integrationActions.deleteMappingSuccess, (state, { companyId }) => {
        const next = state.tenantDetail.aumicoCompanies.filter((c) => c.id !== companyId);
        if (next.length === state.tenantDetail.aumicoCompanies.length) return state;
        return {
            ...state,
            tenantDetail: { ...state.tenantDetail, aumicoCompanies: next },
        };
    }),

    // ── Banner staleness rule ──────────────────────────────────────────────
    //
    // The auto match banner ("X automatisch erkannt") is a transient hint
    // shown right after the post Connect run. Once the user manually edits
    // (upsert) OR unlinks (delete) ANY mapping, that summary is no longer
    // accurate: the underlying counts have changed but we don't recompute.
    // Clearing here is the simplest correctness fix: a single manual action
    // wipes the banner, and the user reads the table directly from then on.
    //
    // NgRx applies multiple `on()` calls for the same action in sequence.
    // This entry runs AFTER the per action handlers above (tenantDetail
    // patches), so the order is: patch tenantDetail → clear banner.
    on(
        integrationActions.upsertMappingSuccess,
        integrationActions.deleteMappingSuccess,
        (state) => {
            if (state.autoMatch.lastResult == null && state.autoMatch.error == null) {
                return state;
            }
            return {
                ...state,
                autoMatch: {
                    ...state.autoMatch,
                    lastResult: null,
                    error: null,
                    connectionId: null,
                },
            };
        },
    ),

    // ── D365 BC journal import ──────────────────────────────────────────────
    on(integrationActions.d365bcFiscalYearsLoad, (state) => ({
        ...state,
        d365bcJournalImport: {
            ...state.d365bcJournalImport,
            fiscalYearsLoading: true,
            fiscalYearsError: null,
        },
    })),
    on(integrationActions.d365bcFiscalYearsLoadSuccess, (state, { fiscalYears }) => ({
        ...state,
        d365bcJournalImport: {
            ...state.d365bcJournalImport,
            fiscalYears,
            fiscalYearsLoading: false,
            fiscalYearsError: null,
        },
    })),
    on(integrationActions.d365bcFiscalYearsLoadFailure, (state, { error }) => ({
        ...state,
        d365bcJournalImport: {
            ...state.d365bcJournalImport,
            fiscalYearsLoading: false,
            fiscalYearsError: error,
        },
    })),
    on(integrationActions.d365bcJournalImportRun, (state) => ({
        ...state,
        d365bcJournalImport: {
            ...state.d365bcJournalImport,
            importStatus: 'loading',
            importResult: null,
            importError: null,
        },
    })),
    on(integrationActions.d365bcJournalImportSuccess, (state, { result }) => ({
        ...state,
        d365bcJournalImport: {
            ...state.d365bcJournalImport,
            importStatus: 'success',
            importResult: result,
            importError: null,
        },
    })),
    on(integrationActions.d365bcJournalImportFailure, (state, { error }) => ({
        ...state,
        d365bcJournalImport: {
            ...state.d365bcJournalImport,
            importStatus: 'error',
            importResult: null,
            importError: error,
        },
    })),
    on(integrationActions.d365bcJournalImportReset, (state) => ({
        ...state,
        d365bcJournalImport: {
            ...state.d365bcJournalImport,
            importStatus: 'idle',
            importResult: null,
            importError: null,
        },
    })),

    // ── Abacus journal import ────────────────────────────────────────────
    on(integrationActions.abacusJournalImportRun, (state) => ({
        ...state,
        abacusJournalImport: {
            ...state.abacusJournalImport,
            importStatus: 'loading',
            importResult: null,
            importError: null,
        },
    })),
    on(integrationActions.abacusJournalImportSuccess, (state, { result }) => ({
        ...state,
        abacusJournalImport: {
            ...state.abacusJournalImport,
            importStatus: 'success',
            importResult: result,
            importError: null,
        },
    })),
    on(integrationActions.abacusJournalImportFailure, (state, { error }) => ({
        ...state,
        abacusJournalImport: {
            ...state.abacusJournalImport,
            importStatus: 'error',
            importResult: null,
            importError: error,
        },
    })),
    on(integrationActions.abacusJournalImportReset, (state) => ({
        ...state,
        abacusJournalImport: {
            importStatus: 'idle',
            importResult: null,
            importError: null,
        },
    })),

    // ── Topal journal import ─────────────────────────────────────────────
    on(integrationActions.topalJournalImportRun, (state) => ({
        ...state,
        topalJournalImport: { importStatus: 'loading', importResult: null, importError: null },
    })),
    on(integrationActions.topalJournalImportSuccess, (state, { result }) => ({
        ...state,
        topalJournalImport: { importStatus: 'success', importResult: result, importError: null },
    })),
    on(integrationActions.topalJournalImportFailure, (state, { error }) => ({
        ...state,
        topalJournalImport: { importStatus: 'error', importResult: null, importError: error },
    })),
    on(integrationActions.topalJournalImportReset, (state) => ({
        ...state,
        topalJournalImport: { importStatus: 'idle', importResult: null, importError: null },
    })),

    // ── Accounto journal import ──────────────────────────────────────────
    on(integrationActions.accountoJournalImportRun, (state, { body, annualReportId }) => ({
        ...state,
        accountoJournalImport: {
            importStatus: 'loading',
            importResult: null,
            importError: null,
            importFor: { companyId: body.companyId, annualReportId },
        },
    })),
    // Success and failure keep `importFor`: the result belongs to the report the run was started for.
    on(integrationActions.accountoJournalImportSuccess, (state, { result }) => ({
        ...state,
        accountoJournalImport: {
            ...state.accountoJournalImport,
            importStatus: 'success',
            importResult: result,
            importError: null,
        },
    })),
    on(integrationActions.accountoJournalImportFailure, (state, { error }) => ({
        ...state,
        accountoJournalImport: {
            ...state.accountoJournalImport,
            importStatus: 'error',
            importResult: null,
            importError: error,
        },
    })),
    on(integrationActions.accountoJournalImportReset, (state) => ({
        ...state,
        accountoJournalImport: { importStatus: 'idle', importResult: null, importError: null, importFor: null },
    })),

    // ── Proffix journal import ───────────────────────────────────────────
    on(integrationActions.proffixJournalImportRun, (state, { body, annualReportId }) => ({
        ...state,
        proffixJournalImport: {
            importStatus: 'loading',
            importResult: null,
            importError: null,
            importFor: { companyId: body.companyId, annualReportId },
        },
    })),
    // Success and failure keep `importFor`: the result belongs to the report the run was started for.
    on(integrationActions.proffixJournalImportSuccess, (state, { result }) => ({
        ...state,
        proffixJournalImport: {
            ...state.proffixJournalImport,
            importStatus: 'success',
            importResult: result,
            importError: null,
        },
    })),
    on(integrationActions.proffixJournalImportFailure, (state, { error }) => ({
        ...state,
        proffixJournalImport: {
            ...state.proffixJournalImport,
            importStatus: 'error',
            importResult: null,
            importError: error,
        },
    })),
    on(integrationActions.proffixJournalImportReset, (state) => ({
        ...state,
        proffixJournalImport: { importStatus: 'idle', importResult: null, importError: null, importFor: null },
    })),

    // ── SharePoint document source (REV-21679) ───────────────────────────────
    //
    // Connect flow: `connecting` while the consent popup is open, then the
    // completed action reloads the connection (which flips to `connected`).
    on(integrationActions.sharepointConnectInitiate, (state) => ({
        ...state,
        sharepoint: {
            ...state.sharepoint,
            connection: { ...state.sharepoint.connection, status: 'connecting' },
            error: undefined,
        },
    })),
    on(integrationActions.sharepointConnectPopupOpened, (state) => ({
        ...state,
        sharepoint: {
            ...state.sharepoint,
            connection: { ...state.sharepoint.connection, status: 'connecting' },
        },
    })),
    // The connection status transition happens on loadConnectionSuccess.
    on(integrationActions.sharepointConnectCompleted, (state) => state),
    on(integrationActions.sharepointConnectFailed, (state, { error }) => ({
        ...state,
        sharepoint: {
            ...state.sharepoint,
            connection: {
                ...state.sharepoint.connection,
                status: 'error',
                errorMessageKey: 'aumico_integration_sharepoint_error',
            },
            error,
        },
    })),

    on(integrationActions.sharepointLoadConnection, (state) => ({
        ...state,
        sharepoint: { ...state.sharepoint, isLoading: true, error: undefined },
    })),
    on(integrationActions.sharepointLoadConnectionSuccess, (state, { connection }) => ({
        ...state,
        sharepoint: { ...state.sharepoint, connection, isLoading: false, error: undefined },
    })),
    on(integrationActions.sharepointLoadConnectionFailure, (state, { error }) => ({
        ...state,
        sharepoint: { ...state.sharepoint, isLoading: false, error },
    })),

    on(integrationActions.sharepointLoadFolders, (state) => ({
        ...state,
        // Keep the previous folder list visible during refresh (same pattern as
        // externalCompanies / tenantDetail) so the list doesn't blank out.
        sharepoint: { ...state.sharepoint, isLoading: true, error: undefined },
    })),
    on(integrationActions.sharepointLoadFoldersSuccess, (state, { folders }) => ({
        ...state,
        sharepoint: { ...state.sharepoint, folders, isLoading: false, error: undefined },
    })),
    on(integrationActions.sharepointLoadFoldersFailure, (state, { error }) => ({
        ...state,
        sharepoint: { ...state.sharepoint, isLoading: false, error },
    })),

    on(integrationActions.sharepointAddFolders, (state) => ({
        ...state,
        sharepoint: { ...state.sharepoint, isLoading: true, error: undefined },
    })),
    /**
     * REV-21057 BUG 3 — APPEND, never replace. The POST response carries only the folders
     * that were actually CREATED (the BE dedupes by drive + folder item), so it is `[]` when
     * the user re-picks a folder that is already bound. Replacing `folders` with the response
     * therefore wiped the list. We merge and de-dupe by `id`, keeping the incoming version of
     * an already-known folder so a re-read can refresh a row in place.
     */
    on(integrationActions.sharepointAddFoldersSuccess, (state, { folders }) => ({
        ...state,
        sharepoint: {
            ...state.sharepoint,
            folders: mergeFoldersById(state.sharepoint.folders, folders),
            isLoading: false,
            error: undefined,
        },
    })),
    on(integrationActions.sharepointAddFoldersFailure, (state, { error }) => ({
        ...state,
        sharepoint: { ...state.sharepoint, isLoading: false, error },
    })),

    // Optimistic: apply immediately, and on failure apply the rolled-back value the effect
    // sends. No isLoading flip — a spinner on a checkbox reads as broken.
    on(
        integrationActions.sharepointUpdateFolder,
        integrationActions.sharepointUpdateFolderSuccess,
        (state, { folderId, includeSubfolders }) => ({
            ...state,
            sharepoint: {
                ...state.sharepoint,
                folders: state.sharepoint.folders.map((f) =>
                    f.id === folderId ? { ...f, includeSubfolders } : f,
                ),
            },
        }),
    ),
    on(integrationActions.sharepointUpdateFolderFailure, (state, { folderId, includeSubfolders, error }) => ({
        ...state,
        sharepoint: {
            ...state.sharepoint,
            folders: state.sharepoint.folders.map((f) => (f.id === folderId ? { ...f, includeSubfolders } : f)),
            error,
        },
    })),

    on(integrationActions.sharepointRemoveFolder, (state) => ({
        ...state,
        sharepoint: { ...state.sharepoint, isLoading: true, error: undefined },
    })),
    on(integrationActions.sharepointRemoveFolderSuccess, (state, { folderId }) => ({
        ...state,
        sharepoint: {
            ...state.sharepoint,
            folders: state.sharepoint.folders.filter((f) => f.id !== folderId),
            isLoading: false,
            error: undefined,
        },
    })),
    on(integrationActions.sharepointRemoveFolderFailure, (state, { error }) => ({
        ...state,
        sharepoint: { ...state.sharepoint, isLoading: false, error },
    })),

    // A NEW fetch clears the previous terminal-failure marker, so a stale failure can never
    // close the popup of a later click (REV-21057 BUG 2). The cached token/baseUrl are
    // deliberately KEPT: the picker seam mints its own per-resource tokens anyway, so the
    // slice value is only a readiness signal — dropping it would re-introduce the wait.
    on(integrationActions.sharepointLoadPickerToken, (state) => ({
        ...state,
        sharepoint: { ...state.sharepoint, pickerTokenError: undefined },
    })),
    on(integrationActions.sharepointLoadPickerTokenSuccess, (state, { token, baseUrl }) => ({
        ...state,
        sharepoint: {
            ...state.sharepoint,
            pickerToken: token,
            pickerBaseUrl: baseUrl,
            pickerTokenError: undefined,
        },
    })),
    on(integrationActions.sharepointLoadPickerTokenFailure, (state, { error }) => ({
        ...state,
        sharepoint: { ...state.sharepoint, error, pickerTokenError: error },
    })),
);

/**
 * Merge newly added folders into the existing list, de-duped by `id` (REV-21057 BUG 3).
 * Existing order is preserved; genuinely new folders are appended in arrival order.
 */
function mergeFoldersById(
    existing: SelectedClosingFolder[],
    incoming: SelectedClosingFolder[],
): SelectedClosingFolder[] {
    const byId = new Map<string, SelectedClosingFolder>();
    for (const folder of existing ?? []) {
        byId.set(folder.id, folder);
    }
    for (const folder of incoming ?? []) {
        byId.set(folder.id, folder);
    }
    return Array.from(byId.values());
}
